/**
 * Simulator store using Zustand
 */

import { create } from 'zustand';
import {
  sendSensorPacket,
  loadCriterion as apiLoadCriterion,
} from '../lib/api';
import { SelectedClip, Criterion, Phase, DiaperModel } from '../lib/types';
import {
  buildSensorPacket,
  RELAY_ADDRESS,
  RELAY_ANDROID_ID,
  findWetnessForPercent,
  calculateWetnessVolume,
} from '../lib/utils';
import { useSessionsStore } from './sessionsStore';

const repeatedPayloadState = new Map<string, { signature: string; repeatCount: number }>();

function uniquifyRepeatedPayload(clipAddress: string, payload: number[]): number[] {
  const signature = JSON.stringify(payload);
  const previous = repeatedPayloadState.get(clipAddress);

  if (!previous || previous.signature !== signature) {
    repeatedPayloadState.set(clipAddress, { signature, repeatCount: 0 });
    return payload;
  }

  const repeatCount = previous.repeatCount + 1;
  repeatedPayloadState.set(clipAddress, { signature, repeatCount });

  const next = [...payload];
  // Nova collapses byte-for-byte duplicate packets even when the send timestamp changes.
  // For deliberate repeat sends from the simulator, bump the packet's diaper counter so
  // Nova records a fresh sample and the same alert can be fired again.
  next[6] = (Number(next[6] || 0) + repeatCount) & 0xff;
  return next;
}

interface PacketLog {
  id: string;
  timestamp: string;
  description: string;
  success: boolean;
}

interface SimulatorState {
  selectedClip: SelectedClip | null;
  currentCriterion: Criterion | null;
  currentPhase: Phase | null;
  changeNeededPercent: number | null;
  
  // Packet builder state
  wetnessLevel: number;
  statusCode: number;
  battery: number;
  diaperCounter: number;
  adcValues: number[];
  
  // Packet log
  packetLog: PacketLog[];
  
  // Loading states
  isLoadingCriterion: boolean;
  isSendingPacket: boolean;
  
  // Actions
  selectClip: (clip: SelectedClip | null) => void;
  updateSelectedClipFromSession: (sessionId: number, updatedClip: SelectedClip) => void;
  setWetnessLevel: (level: number) => void;
  setStatusCode: (code: number) => void;
  setBattery: (level: number) => void;
  setDiaperCounter: (counter: number) => void;
  setAdcValues: (values: number[]) => void;
  loadCriterion: (criterionId: number) => Promise<void>;
  selectPhase: (phaseId: number, diaperModelCode: number | null) => void;
  sendPacket: (description: string, payload?: number[]) => Promise<void>;
  sendQuickAction: (action: string, diaperModel: DiaperModel | null) => Promise<void>;
  clearLog: () => void;
}

export const useSimulatorStore = create<SimulatorState>((set, get) => ({
  selectedClip: null,
  currentCriterion: null,
  currentPhase: null,
  changeNeededPercent: null,
  
  wetnessLevel: 0,
  statusCode: 0,
  battery: 80,
  diaperCounter: 30,
  adcValues: [3, 2, 1, 1, 0],
  
  packetLog: [],
  isLoadingCriterion: false,
  isSendingPacket: false,

  selectClip: (clip: SelectedClip | null) => {
    if (!clip) {
      set({
        selectedClip: null,
        currentCriterion: null,
        currentPhase: null,
        changeNeededPercent: null,
        packetLog: [],
      });
      return;
    }

    set({
      selectedClip: clip,
      wetnessLevel: clip.wetnessLevel || 0,
      statusCode: clip.statusCode || 0,
      battery: clip.clipBatteryPercent || 80,
      diaperCounter: clip.diaperCounter || 30,
      // Wipe stale criterion/phase from a previously selected clip so the UI
      // does not flash the wrong threshold while the new criterion loads.
      currentCriterion: null,
      currentPhase: null,
      changeNeededPercent: null,
    });
    // Criterion fetching depends on a Nova account header; SimulatorPage owns
    // the (account, clip) effect and calls loadCriterion when both are ready.
  },

  updateSelectedClipFromSession: (sessionId: number, updatedClip: SelectedClip) => {
    const { selectedClip } = get();
    
    // Only update if this is the currently selected clip
    if (selectedClip && selectedClip.id === sessionId) {
      set({
        selectedClip: updatedClip,
        wetnessLevel: updatedClip.wetnessLevel || 0,
        statusCode: updatedClip.statusCode || 0,
        battery: updatedClip.clipBatteryPercent || 80,
        diaperCounter: updatedClip.diaperCounter || 30,
      });
    }
  },

  setWetnessLevel: (level: number) => set({ wetnessLevel: level }),
  setStatusCode: (code: number) => set({ statusCode: code }),
  setBattery: (level: number) => set({ battery: level }),
  setDiaperCounter: (counter: number) => set({ diaperCounter: counter }),
  setAdcValues: (values: number[]) => {
    // Ensure we have exactly 5 ADC values
    const clampedValues = values.slice(0, 5).map(v => Math.max(0, Math.min(255, v)));
    while (clampedValues.length < 5) {
      clampedValues.push(0);
    }
    set({ adcValues: clampedValues });
  },

  loadCriterion: async (criterionId: number) => {
    set({ isLoadingCriterion: true });
    try {
      const { currentNovaAccountId } = useSessionsStore.getState();
      if (!currentNovaAccountId) {
        throw new Error('No Nova account selected — cannot load criterion');
      }
      const criterion = await apiLoadCriterion(criterionId, { novaAccountId: currentNovaAccountId });
      set({ currentCriterion: criterion, isLoadingCriterion: false });

      const { selectedClip } = get();
      if (criterion.content?.phases?.length > 0) {
        const defaultPhase = criterion.content.phases.find(
          (p: Phase) => p.name && p.name.toLowerCase() === 'default'
        );
        const phase = defaultPhase || criterion.content.phases[0];
        get().selectPhase(phase.id, selectedClip?.diaperModelCode || null);
      }
    } catch (error: any) {
      console.error('Failed to load criterion:', error);
      set({ isLoadingCriterion: false });
      throw error;
    }
  },

  selectPhase: (phaseId: number, diaperModelCode: number | null) => {
    const { currentCriterion } = get();
    if (!currentCriterion) return;

    const phase = currentCriterion.content.phases.find((p) => p.id === phaseId);
    if (!phase) return;

    // Find threshold for current diaper model or default
    let threshold = phase.thresholds.find((t) => t.diaperModel === diaperModelCode);
    if (!threshold) {
      threshold = phase.thresholds.find((t) => t.diaperModel == null);
    }

    set({
      currentPhase: phase,
      changeNeededPercent: threshold?.percent || null,
    });
  },

  sendPacket: async (description: string, payload?: number[]) => {
    const { selectedClip, wetnessLevel, statusCode, battery, diaperCounter, adcValues } = get();
    if (!selectedClip) throw new Error('No clip selected');

    set({ isSendingPacket: true });

    const basePayload = payload || buildSensorPacket({
      wetness: wetnessLevel,
      statusCode,
      battery,
      diaperModel: selectedClip.diaperModelCode || 30,
      diaperCounter,
      adcValues,
      firmwareRevision: selectedClip.firmwareRevision,
    });
    const actualPayload = uniquifyRepeatedPayload(selectedClip.clipAddress, basePayload);

    const logId = Math.random().toString(36).substring(7);
    const timestamp = new Date().toISOString();

    try {
      const { currentNovaAccountId } = useSessionsStore.getState();
      await sendSensorPacket({
        payload: actualPayload,
        time: timestamp,
        rssi: -55,
        senderAddress: selectedClip.clipAddress,
        receiverAddress: RELAY_ADDRESS,
        receiverAndroidId: RELAY_ANDROID_ID,
      }, { novaAccountId: currentNovaAccountId });

      // Add success log
      set((state) => ({
        packetLog: [
          {
            id: logId,
            timestamp,
            description,
            success: true,
          },
          ...state.packetLog.slice(0, 49), // Keep last 50 entries
        ],
        isSendingPacket: false,
      }));

      // Auto-refresh sessions after successful packet send (2 second delay)
      setTimeout(() => {
        useSessionsStore.getState().refreshSessions();
      }, 2000);
    } catch (error: any) {
      // Add error log
      set((state) => ({
        packetLog: [
          {
            id: logId,
            timestamp,
            description: `${description} - Error: ${error.message}`,
            success: false,
          },
          ...state.packetLog.slice(0, 49),
        ],
        isSendingPacket: false,
      }));
      throw error;
    }
  },

  sendQuickAction: async (action: string, diaperModel: DiaperModel | null) => {
    const { selectedClip, diaperCounter, changeNeededPercent } = get();
    if (!selectedClip) throw new Error('No clip selected');

    let wetness = 0;
    let statusCode = 0;
    let battery = 80;
    let description = '';
    let customDiaperModelCode: number | null = null; // Allow overriding diaper model code

    switch (action) {
      case 'OK':
        wetness = 0;
        description = 'OK (0%)';
        break;
      case 'WET':
        // Always send 10% wetness
        if (diaperModel) {
          wetness = findWetnessForPercent(10, diaperModel);
          description = 'Wet Diaper (10%)';
        } else {
          description = 'Wet Diaper (10%)';
        }
        break;
      case 'CHANGE_NEEDED':
        if (diaperModel && changeNeededPercent != null) {
          // Send threshold + 5%
          const targetPercent = Math.min(100, changeNeededPercent + 5);
          wetness = findWetnessForPercent(targetPercent, diaperModel);
          description = `Change Needed (${targetPercent.toFixed(0)}%)`;
        } else {
          description = 'Change Needed';
        }
        break;
      case 'LEAK_IMMINENT':
        if (diaperModel?.leakThreshold?.wetnessLevel) {
          // Use leak threshold from diaper model + small increment
          // This respects the defined leak threshold, not 100% capacity
          wetness = Math.min(32767, diaperModel.leakThreshold.wetnessLevel + 100);
          const targetPercent = calculateWetnessVolume(wetness, diaperModel).percent;
          if (targetPercent != null) {
            description = `Leak Imminent (${Math.round(targetPercent)}%)`;
          } else {
            description = 'Leak Imminent';
          }
        } else {
          description = 'Leak Imminent';
        }
        break;
      case 'DETACHED':
        statusCode = 1;
        description = 'Clip Detached';
        break;
      case 'LOW_BATTERY':
        battery = 5;
        description = 'Low Battery (5%)';
        break;
      case 'INVALID_DIAPER':
        // Invalid diaper is triggered when backend receives diaper model code 0
        // Per Postman test collection: status 0, model 0, wetness 80
        statusCode = 0;
        wetness = 80;
        customDiaperModelCode = 0; // Model 0 = invalid/unknown diaper
        description = 'Invalid Diaper (model 0, wetness 80)';
        break;
      case 'SHORTED':
        statusCode = 2;
        description = 'Clip Shorted';
        break;
      default:
        throw new Error(`Unknown action: ${action}`);
    }

    const payload = buildSensorPacket({
      wetness,
      statusCode,
      battery,
      diaperModel: customDiaperModelCode !== null ? customDiaperModelCode : (selectedClip.diaperModelCode || 30),
      diaperCounter,
      firmwareRevision: selectedClip.firmwareRevision,
    });

    await get().sendPacket(description, payload);
  },

  clearLog: () => set({ packetLog: [] }),
}));
